<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import RewardFormBlock from '@/components/rewards/RewardFormBlock.vue'

import { DAYS_PER_MONTH, DAYS_PER_WEEK, parseUsageLimit } from '@/components/rewards/utils/offerRewardHydration'
import { validateUsageLimitQuantity } from '@/components/rewards/utils/offerRewardValidation'

import type { RadioCardOption } from '@/components/ui/AppRadioCard.vue'
import type { UsageLimit } from '@/types/offer'

const { t } = useI18n()

const props = defineProps<{
  claimLimitQuantity: number | null
  claimLimitDays: number | null
  formTouched?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:claimLimitQuantity', value: number | null): void
  (e: 'update:claimLimitDays', value: number | null): void
  (e: 'update:formTouched', value: boolean): void
}>()

const claimLimitQuantity = useVModel(props, 'claimLimitQuantity', emit)
const claimLimitDays = useVModel(props, 'claimLimitDays', emit)

const selectedFrequencyUnit = ref(1)
const frequencyTimesPerUnit = ref(1)
const usageLimit = ref<UsageLimit>('none')
const claimLimitQuantityTouched = ref(false)

const frequencyUnits = [
  { id: 1, text: t('pages.loyaltyPage.edit.claimLimitTimeUnit.days') },
  { id: DAYS_PER_WEEK, text: t('pages.loyaltyPage.edit.claimLimitTimeUnit.weeks') },
  { id: DAYS_PER_MONTH, text: t('pages.loyaltyPage.edit.claimLimitTimeUnit.months') }
]

const usageLimitOptions = [
  {
    id: 'none',
    title: t('pages.offer.reward.usageLimitNone'),
    description: t('pages.offer.reward.usageLimitNoneDesc')
  },
  {
    id: 'quantity',
    title: t('pages.offer.reward.usageLimitQuantity'),
    description: t('pages.offer.reward.usageLimitQuantityDesc')
  },
  {
    id: 'unlimited',
    title: t('pages.offer.reward.usageLimitUnlimited'),
    description: t('pages.offer.reward.usageLimitUnlimitedDesc')
  }
]

const hasUsageLimitQuantityError = computed(() => {
  if (usageLimit.value !== 'unlimited') return false
  return !validateUsageLimitQuantity(claimLimitQuantity.value)
})

const usageLimitQuantityErrors = computed(() =>
  claimLimitQuantityTouched.value && hasUsageLimitQuantityError.value
    ? [t('pages.offer.reward.usageLimitQuantityError')]
    : []
)

const selectedUsageLimitOption = computed({
  get: () => usageLimitOptions.find((o) => o.id === usageLimit.value),
  set: (opt: RadioCardOption & { id: UsageLimit }) => {
    if (opt?.id) handleUsageLimitChange(opt.id)
  }
})

const markFormTouched = () => {
  if (props.formTouched !== undefined) {
    emit('update:formTouched', true)
  }
}

const handleUsageLimitChange = (val: UsageLimit) => {
  usageLimit.value = val
  markFormTouched()

  if (val === 'none') {
    claimLimitQuantity.value = null
    claimLimitDays.value = null
  } else if (val === 'unlimited') {
    claimLimitQuantity.value = claimLimitQuantity.value ?? 1
    claimLimitDays.value = frequencyTimesPerUnit.value * selectedFrequencyUnit.value || 7
  } else {
    claimLimitQuantity.value = 1
    claimLimitDays.value = null
  }
}

const handleFrequencyUnitChange = (v: number) => {
  selectedFrequencyUnit.value = v
  updateFrequencyClaimLimit()
}

const handleFrequencyTimesChange = (v: number) => {
  frequencyTimesPerUnit.value = v
  updateFrequencyClaimLimit()
}

const updateFrequencyClaimLimit = () => {
  if (usageLimit.value !== 'unlimited') return
  claimLimitDays.value = frequencyTimesPerUnit.value * selectedFrequencyUnit.value
  markFormTouched()
}

const handleClaimLimitQuantityChange = (val: number | null) => {
  claimLimitQuantity.value = val
  claimLimitQuantityTouched.value = true
  markFormTouched()
}

const hydrateFromValues = () => {
  const parsed = parseUsageLimit({
    claimLimitQuantity: claimLimitQuantity.value,
    claimLimitDays: claimLimitDays.value
  })
  usageLimit.value = parsed.usageLimit
  selectedFrequencyUnit.value = parsed.frequencyUnit
  frequencyTimesPerUnit.value = parsed.frequencyTimes
  claimLimitQuantityTouched.value = false
}

watch(
  () => [claimLimitQuantity.value, claimLimitDays.value] as const,
  (next, prev) => {
    if (next[0] === prev?.[0] && next[1] === prev?.[1]) return
    hydrateFromValues()
  },
  { immediate: true, flush: 'post' }
)
</script>

<template>
  <RewardFormBlock :title="t('pages.offer.reward.claimLimit')">
    <AppRadioCard v-model="selectedUsageLimitOption" :options="usageLimitOptions">
      <template #1="{ checked }">
        <div class="flex min-w-0 flex-col gap-1">
          <span class="truncate text-sm font-medium text-foreground">
            {{ t('pages.offer.reward.usageLimitQuantity') }}
          </span>
          <span class="text-xs text-muted-foreground">
            {{ t('pages.offer.reward.usageLimitQuantityDesc') }}
          </span>
          <div v-if="checked" class="mt-5 flex flex-col gap-2" @click.stop>
            <div class="flex flex-row items-center gap-2">
              <span class="text-xs text-foreground-secondary">
                {{ t('pages.offer.reward.usageLimitQuantityPrefix') }}
              </span>
              <div>
                <AppFormInput
                  id="offer-claimLimitQuantity"
                  :model-value="claimLimitQuantity"
                  type="number"
                  placeholder="100"
                  :min="1"
                  class="w-12"
                  input-classes="px-2! py-1! h-auto! number-to-text"
                  :errors="usageLimitQuantityErrors"
                  error-classes="!mt-1 !text-xs"
                  @update:model-value="handleClaimLimitQuantityChange($event as number | null)"
                />
              </div>
              <span class="text-sm text-foreground-secondary">
                {{ t('pages.offer.reward.usageLimitQuantitySuffix') }}
              </span>
            </div>
          </div>
        </div>
      </template>

      <template #2="{ checked }">
        <div class="flex min-w-0 flex-col gap-1">
          <span class="truncate text-sm font-medium text-foreground">
            {{ t('pages.offer.reward.usageLimitUnlimited') }}
          </span>
          <span class="text-xs text-muted-foreground">
            {{ t('pages.offer.reward.usageLimitUnlimitedDesc') }}
          </span>
          <div v-if="checked" class="mt-5 flex flex-col gap-2" @click.stop>
            <div class="flex flex-row flex-wrap items-center gap-2">
              <span class="text-xs text-foreground-secondary">
                {{ t('pages.offer.reward.usageLimitQuantityPrefix') }}
              </span>
              <div class="flex">
                <AppFormInput
                  id="offer-claimLimitQuantity-unlimited"
                  :model-value="claimLimitQuantity"
                  type="number"
                  placeholder="100"
                  :min="1"
                  class="w-12"
                  input-classes="px-2! py-1! h-auto! number-to-text"
                  :errors="usageLimitQuantityErrors"
                  error-classes="!mt-1 !text-xs"
                  @update:model-value="handleClaimLimitQuantityChange($event as number | null)"
                />
              </div>
              <span class="text-xs text-foreground-secondary">
                {{ t('pages.offer.reward.usageLimitQuantitySuffix') }}{{ t('pages.offer.reward.usageLimitPeriod') }}
              </span>
              <AppFormInput
                id="offer-frequency-times"
                :model-value="frequencyTimesPerUnit"
                type="number"
                :min="1"
                placeholder="3"
                class="w-12"
                input-classes="px-2! py-1! h-auto! number-to-text"
                @update:model-value="handleFrequencyTimesChange($event as number)"
              />
              <Select
                :model-value="String(selectedFrequencyUnit)"
                @update:model-value="(v) => handleFrequencyUnitChange(Number(v))"
              >
                <SelectTrigger class="w-20 px-2! py-1! h-auto!">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="unit in frequencyUnits" :key="unit.id" :value="String(unit.id)">
                    {{ unit.text }}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </template>
    </AppRadioCard>
  </RewardFormBlock>
</template>

<style>
input.number-to-text::-webkit-outer-spin-button,
input.number-to-text::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'].number-to-text {
  -moz-appearance: textfield;
}
</style>
